/**
 * Channel Manager Component
 * არხების მართვის კომპონენტი
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Button, Modal, Input, Spinner, useNotification } from '@aiia/portal-core';
import { getApiErrorMessage } from '../utils/errorMessages';
import { formatDateTime } from '../utils/formatDate';
import {
  useWidgetChannels,
  useConnectTelegram,
  useConnectWhatsApp,
  useConnectViber,
  useConnectMessenger,
  useConnectInstagram,
  useUpdateChannelStatus,
  useDisconnectChannel,
} from '../hooks/useChannels';
import {
  type ChannelType,
  type ChannelConfig,
  CHANNEL_INFO,
} from '../features/api/channels';

interface ChannelManagerProps {
  widgetId: string;
}

export function ChannelManager({ widgetId }: ChannelManagerProps) {
  const { channels, isLoading, refetch } = useWidgetChannels(widgetId);
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [selectedChannelType, setSelectedChannelType] = useState<ChannelType | null>(null);

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Spinner size="lg" />
      </div>
    );
  }

  const connectedTypes = channels.map((c) => c.channelType);
  const availableChannels = Object.values(CHANNEL_INFO).filter(
    (info) => !connectedTypes.includes(info.type)
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Messaging Channels</h3>
          <p className="text-sm text-gray-500">
            Connect messaging platforms to receive messages from your customers
          </p>
        </div>
        {availableChannels.length > 0 && (
          <Button onClick={() => setShowConnectModal(true)}>
            + Add Channel
          </Button>
        )}
      </div>

      {/* Connected Channels */}
      {channels.length > 0 ? (
        <div className="grid gap-4">
          {channels.map((channel) => (
            <ChannelCard
              key={channel.id}
              channel={channel}
              onUpdate={() => refetch()}
            />
          ))}
        </div>
      ) : (
        <Card className="p-8 text-center">
          <div className="text-4xl mb-4">📱</div>
          <h4 className="text-lg font-medium text-gray-900 mb-2">
            No channels connected
          </h4>
          <p className="text-sm text-gray-500 mb-4">
            Connect Telegram, WhatsApp, or other messaging platforms to start receiving messages
          </p>
          <Button onClick={() => setShowConnectModal(true)}>
            Connect Your First Channel
          </Button>
        </Card>
      )}

      {/* Connect Channel Modal */}
      <Modal
        isOpen={showConnectModal}
        onClose={() => {
          setShowConnectModal(false);
          setSelectedChannelType(null);
        }}
        title={selectedChannelType ? `Connect ${CHANNEL_INFO[selectedChannelType].name}` : 'Add Channel'}
      >
        {!selectedChannelType ? (
          <ChannelSelector
            availableChannels={availableChannels}
            onSelect={(type) => setSelectedChannelType(type)}
          />
        ) : (
          <ConnectChannelForm
            widgetId={widgetId}
            channelType={selectedChannelType}
            onSuccess={() => {
              setShowConnectModal(false);
              setSelectedChannelType(null);
              refetch();
            }}
            onBack={() => setSelectedChannelType(null)}
          />
        )}
      </Modal>
    </div>
  );
}

// =============== Channel Card ===============

interface ChannelCardProps {
  channel: ChannelConfig;
  onUpdate: () => void;
}

function ChannelCard({ channel, onUpdate }: ChannelCardProps) {
  const { t } = useTranslation();
  const info = CHANNEL_INFO[channel.channelType];
  const updateStatus = useUpdateChannelStatus();
  const disconnect = useDisconnectChannel();
  const notification = useNotification();
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [showWebhookInfo, setShowWebhookInfo] = useState(false);
  const needsManualWebhook = ['MESSENGER', 'INSTAGRAM', 'WHATSAPP', 'VIBER'].includes(channel.channelType);

  const handleToggle = async () => {
    try {
      await updateStatus.mutateAsync({
        channelId: channel.id,
        data: { enabled: channel.status !== 'ACTIVE' },
      });
      notification.success(
        channel.status === 'ACTIVE' ? 'Channel disabled' : 'Channel enabled'
      );
      onUpdate();
    } catch (error: unknown) {
      notification.error(getApiErrorMessage(error, t));
    }
  };

  const handleDisconnect = async () => {
    try {
      await disconnect.mutateAsync(channel.id);
      notification.success('Channel disconnected');
      onUpdate();
    } catch (error: unknown) {
      notification.error(getApiErrorMessage(error, t));
    }
  };

  const statusColors: Record<string, string> = {
    ACTIVE: 'bg-green-100 text-green-800',
    PENDING: 'bg-yellow-100 text-yellow-800',
    DISABLED: 'bg-gray-100 text-gray-800',
    ERROR: 'bg-red-100 text-red-800',
  };

  return (
    <>
      <Card className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div
              className="w-12 h-12 rounded-lg flex items-center justify-center text-2xl"
              style={{ backgroundColor: `${info.color}20` }}
            >
              {info.icon}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h4 className="font-medium text-gray-900">{info.name}</h4>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[channel.status]}`}>
                  {channel.status}
                </span>
              </div>
              <p className="text-sm text-gray-500">
                {channel.botUsername}
              </p>
              {channel.lastActivityAt && (
                <p className="text-xs text-gray-400">
                  Last activity: {formatDateTime(channel.lastActivityAt)}
                </p>
              )}
              {channel.errorMessage && (
                <p className="text-xs text-red-500 mt-1">
                  {channel.errorMessage}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {needsManualWebhook && channel.webhookUrl && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowWebhookInfo(!showWebhookInfo)}
              >
                Webhook Info
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={handleToggle}
              disabled={updateStatus.isPending}
            >
              {channel.status === 'ACTIVE' ? 'Disable' : 'Enable'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="text-red-600 hover:bg-red-50"
              onClick={() => setShowDisconnectConfirm(true)}
            >
              Disconnect
            </Button>
          </div>
        </div>
        {showWebhookInfo && channel.webhookUrl && (
          <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200 space-y-3">
            <p className="text-xs font-medium text-gray-700">
              Paste these values in your {info.name} developer console:
            </p>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Callback URL</label>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-xs bg-white px-3 py-2 rounded border border-gray-300 break-all">
                  {channel.webhookUrl}
                </code>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(channel.webhookUrl!);
                    notification.success('Copied to clipboard');
                  }}
                >
                  Copy
                </Button>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Verify Token</label>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-xs bg-white px-3 py-2 rounded border border-gray-300 break-all">
                  {channel.verifyToken}
                </code>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(channel.verifyToken!);
                    notification.success('Copied to clipboard');
                  }}
                >
                  Copy
                </Button>
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Disconnect Confirmation Modal */}
      <Modal
        isOpen={showDisconnectConfirm}
        onClose={() => setShowDisconnectConfirm(false)}
        title="Disconnect Channel"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Are you sure you want to disconnect <strong>{info.name}</strong>?
            This will remove the webhook and stop receiving messages.
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowDisconnectConfirm(false)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleDisconnect}
              disabled={disconnect.isPending}
            >
              {disconnect.isPending ? 'Disconnecting...' : 'Disconnect'}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}

// =============== Channel Selector ===============

interface ChannelSelectorProps {
  availableChannels: typeof CHANNEL_INFO[ChannelType][];
  onSelect: (type: ChannelType) => void;
}

function ChannelSelector({ availableChannels, onSelect }: ChannelSelectorProps) {
  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500">
        Select a messaging platform to connect:
      </p>
      <div className="grid gap-3">
        {availableChannels.map((info) => (
          <button
            key={info.type}
            onClick={() => onSelect(info.type)}
            className="flex items-center gap-4 p-4 border rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors text-left"
          >
            <div
              className="w-12 h-12 rounded-lg flex items-center justify-center text-2xl"
              style={{ backgroundColor: `${info.color}20` }}
            >
              {info.icon}
            </div>
            <div>
              <h4 className="font-medium text-gray-900">{info.name}</h4>
              <p className="text-sm text-gray-500">{info.description}</p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// =============== Connect Channel Form ===============

interface ConnectChannelFormProps {
  widgetId: string;
  channelType: ChannelType;
  onSuccess: () => void;
  onBack: () => void;
}

function ConnectChannelForm({ widgetId, channelType, onSuccess, onBack }: ConnectChannelFormProps) {
  const { t } = useTranslation();
  const info = CHANNEL_INFO[channelType];
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const notification = useNotification();

  const connectTelegram = useConnectTelegram();
  const connectWhatsApp = useConnectWhatsApp();
  const connectViber = useConnectViber();
  const connectMessenger = useConnectMessenger();
  const connectInstagram = useConnectInstagram();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      switch (channelType) {
        case 'TELEGRAM':
          await connectTelegram.mutateAsync({
            widgetId,
            botToken: formData.botToken,
          });
          break;
        case 'WHATSAPP':
          await connectWhatsApp.mutateAsync({
            widgetId,
            phoneNumberId: formData.phoneNumberId,
            accessToken: formData.accessToken,
          });
          break;
        case 'VIBER':
          await connectViber.mutateAsync({
            widgetId,
            authToken: formData.authToken,
          });
          break;
        case 'MESSENGER':
          await connectMessenger.mutateAsync({
            widgetId,
            pageAccessToken: formData.pageAccessToken,
          });
          break;
        case 'INSTAGRAM':
          await connectInstagram.mutateAsync({
            widgetId,
            pageAccessToken: formData.pageAccessToken,
          });
          break;
      }

      notification.success(`${info.name} connected successfully!`);
      onSuccess();
    } catch (error: unknown) {
      notification.error(getApiErrorMessage(error, t));
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = info.fields.every((field) => formData[field.name]?.trim());

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="flex items-center gap-3 p-3 rounded-lg" style={{ backgroundColor: `${info.color}10` }}>
        <span className="text-2xl">{info.icon}</span>
        <div>
          <h4 className="font-medium">{info.name}</h4>
          <p className="text-sm text-gray-500">{info.description}</p>
        </div>
      </div>

      {info.fields.map((field) => (
        <div key={field.name}>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {field.label}
          </label>
          <Input
            type={field.type}
            placeholder={field.placeholder}
            value={formData[field.name] || ''}
            onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
          />
          {field.helpText && (
            <p className="mt-1 text-xs text-gray-500">{field.helpText}</p>
          )}
        </div>
      ))}

      <div className="flex justify-between pt-4">
        <Button type="button" variant="outline" onClick={onBack}>
          Back
        </Button>
        <Button type="submit" disabled={!isFormValid || isSubmitting}>
          {isSubmitting ? 'Connecting...' : 'Connect'}
        </Button>
      </div>
    </form>
  );
}

export default ChannelManager;
